import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import {
  Modal,
  Drawer,
  MultiSelect,
  PlanLockedCard,
  Select,
  useConfirm,
  useToast,
} from '@/components/ui';
import { approvalsApi, Approval, ApprovalKind, ApprovalStatus } from '@/lib/approvals';
import { companiesApi } from '@/lib/companies';
import { usePlan } from '@/hooks/usePlan';
import { useCurrentUser } from '@/hooks/useAuth';
import { formatLocaleDate } from '@/lib/date';

const STATUS_COLOR: Record<ApprovalStatus, string> = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-rose-50 text-rose-700',
  cancelled: 'bg-slate-100 text-slate-600',
};

const STATUS_KEY: Record<ApprovalStatus, string> = {
  pending: 'approvals.status_pending',
  approved: 'approvals.status_approved',
  rejected: 'approvals.status_rejected',
  cancelled: 'approvals.status_cancelled',
};

const KIND_OPTIONS: { value: ApprovalKind; labelKey: string }[] = [
  { value: 'deal_discount', labelKey: 'approvals.kind_deal_discount' },
  { value: 'outbound_template', labelKey: 'approvals.kind_outbound_template' },
  { value: 'board_report', labelKey: 'approvals.kind_board_report' },
  { value: 'contract_terms', labelKey: 'approvals.kind_contract_terms' },
];

export function ApprovalsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const plan = usePlan();
  const me = useCurrentUser();
  const planAllows = plan.meets('global');
  const companyId = me.data?.companyId;

  const [createOpen, setCreateOpen] = useState(false);
  const [drawerApproval, setDrawerApproval] = useState<Approval | null>(null);

  const list = useQuery({
    queryKey: ['approvals', wsId],
    queryFn: () => approvalsApi.list(wsId),
    enabled: !!wsId && planAllows,
  });

  const members = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () =>
      companyId ? companiesApi.members(companyId) : Promise.resolve([]),
    enabled: !!companyId && planAllows,
  });

  const memberOptions = useMemo(
    () =>
      (members.data ?? []).map((m) => ({
        value: m.id,
        label: m.displayName || m.email,
        hint: m.email,
        prefix: (
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ink-200 text-[10px] font-semibold uppercase text-ink-600">
            {(m.displayName || m.email).trim().slice(0, 1)}
          </span>
        ),
        searchAliases: [m.email, m.role],
      })),
    [members.data],
  );
  const memberById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of members.data ?? []) m.set(u.id, u.displayName || u.email);
    return m;
  }, [members.data]);

  const act = useMutation({
    mutationFn: ({
      id,
      action,
      note,
    }: {
      id: string;
      action: 'approve' | 'reject';
      note?: string;
    }) => approvalsApi.act(id, action, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals', wsId] });
      setDrawerApproval(null);
    },
    onError: () => toast.error(t('approvals.act_failed')),
  });

  const create = useMutation({
    mutationFn: (input: {
      targetKind: ApprovalKind;
      targetId: string;
      requiredSigners: string[];
    }) => approvalsApi.create({ workspaceId: wsId, ...input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals', wsId] });
      setCreateOpen(false);
      toast.success(t('approvals.created'));
    },
    onError: () => toast.error(t('approvals.create_failed')),
  });

  const items: Approval[] = list.data ?? [];

  async function rejectWithConfirm(id: string) {
    const ok = await confirm({
      title: t('approvals.confirm_reject_title'),
      description: t('approvals.confirm_reject_body'),
      confirmLabel: t('approvals.act_reject'),
      variant: 'danger',
    });
    if (ok) act.mutate({ id, action: 'reject' });
  }

  if (!plan.isLoading && !planAllows) {
    return (
      <AppShell titleKey="approvals.title">
        <PlanLockedCard
          required="global"
          description={t('plan.locked_approvals_body')}
        />
      </AppShell>
    );
  }

  return (
    <AppShell titleKey="approvals.title">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-ink-600 max-w-2xl">{t('approvals.intro')}</p>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
        >
          {t('approvals.new_button')}
        </button>
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500 max-w-3xl">
          <p className="text-sm">{t('approvals.empty_title')}</p>
          <p className="text-xs mt-2">{t('approvals.empty_sub')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('approvals.col_target')}</th>
                <th className="py-2.5 px-3 font-medium">{t('approvals.col_kind')}</th>
                <th className="py-2.5 px-3 font-medium">{t('approvals.col_status')}</th>
                <th className="py-2.5 px-3 font-medium">{t('approvals.col_step')}</th>
                <th className="py-2.5 px-3 font-medium">{t('approvals.col_signers')}</th>
                <th className="py-2.5 px-3 font-medium">{t('approvals.col_created')}</th>
                <th className="py-2.5 px-3 font-medium">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                  onClick={() => setDrawerApproval(a)}
                >
                  <td className="py-2.5 px-3 num text-ink-700">
                    {a.targetId.slice(0, 8)}…
                  </td>
                  <td className="py-2.5 px-3 text-ink-700">
                    {t(`approvals.kind_${a.targetKind}`, {
                      defaultValue: a.targetKind,
                    })}
                  </td>
                  <td className="py-2.5 px-3">
                    <span
                      className={`text-[10px] uppercase tracking-wide font-semibold rounded-md px-2 py-0.5 ${STATUS_COLOR[a.status]}`}
                    >
                      {t(STATUS_KEY[a.status])}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700">
                    {a.currentStep + 1} / {a.requiredSigners.length}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500">{a.requiredSigners.length}</td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {formatLocaleDate(a.createdAt, i18n.language)}
                  </td>
                  <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
                    {a.status === 'pending' && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => act.mutate({ id: a.id, action: 'approve' })}
                          disabled={act.isPending}
                          className="text-xs px-2 py-1 rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                        >
                          {t('approvals.act_approve')}
                        </button>
                        <button
                          onClick={() => rejectWithConfirm(a.id)}
                          disabled={act.isPending}
                          className="text-xs px-2 py-1 rounded bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
                        >
                          {t('approvals.act_reject')}
                        </button>
                      </div>
                    )}
                    {a.status === 'approved' && (
                      <button
                        onClick={() => {
                          approvalsApi
                            .downloadHankoPdf(a.id)
                            .catch(() => toast.error(t('approvals.act_failed')));
                        }}
                        className="text-xs px-2 py-1 rounded border border-ink-300 text-ink-700 hover:bg-ink-50"
                      >
                        {t('approvals.download_hanko')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('approvals.new_title')}
        size="md"
      >
        <CreateApprovalForm
          kindOptions={KIND_OPTIONS.map((k) => ({
            value: k.value,
            label: t(k.labelKey, { defaultValue: k.value }),
          }))}
          memberOptions={memberOptions}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(v) => create.mutate(v)}
          submitting={create.isPending}
        />
      </Modal>

      <Drawer
        open={!!drawerApproval}
        onOpenChange={(o) => !o && setDrawerApproval(null)}
        title={
          drawerApproval
            ? t(`approvals.kind_${drawerApproval.targetKind}`, {
                defaultValue: drawerApproval.targetKind,
              })
            : ''
        }
      >
        {drawerApproval && (
          <ApprovalDetail
            approval={drawerApproval}
            memberById={memberById}
            onApprove={(note) =>
              act.mutate({ id: drawerApproval.id, action: 'approve', note })
            }
            onReject={async (note) => {
              const ok = await confirm({
                title: t('approvals.confirm_reject_title'),
                description: t('approvals.confirm_reject_body'),
                confirmLabel: t('approvals.act_reject'),
                variant: 'danger',
              });
              if (ok) act.mutate({ id: drawerApproval.id, action: 'reject', note });
            }}
            busy={act.isPending}
          />
        )}
      </Drawer>
    </AppShell>
  );
}

function CreateApprovalForm({
  kindOptions,
  memberOptions,
  onCancel,
  onSubmit,
  submitting,
}: {
  kindOptions: { value: string; label: string }[];
  memberOptions: { value: string; label: string; hint?: string }[];
  onCancel: () => void;
  onSubmit: (v: {
    targetKind: ApprovalKind;
    targetId: string;
    requiredSigners: string[];
  }) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [targetKind, setTargetKind] = useState<ApprovalKind>('deal_discount');
  const [targetId, setTargetId] = useState('');
  const [signers, setSigners] = useState<string[]>([]);

  const canSubmit =
    !submitting && targetId.trim().length > 0 && signers.length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) {
          onSubmit({ targetKind, targetId: targetId.trim(), requiredSigners: signers });
        }
      }}
      className="space-y-4 text-sm"
    >
      <label className="block">
        <span className="text-xs text-ink-600">{t('approvals.field_kind')}</span>
        <div className="mt-1">
          <Select
            value={targetKind}
            onChange={(v) => setTargetKind(v as ApprovalKind)}
            options={kindOptions}
          />
        </div>
      </label>

      <label className="block">
        <span className="text-xs text-ink-600">
          {t('approvals.field_target_id')}{' '}
          <span className="text-warn-600">*</span>
        </span>
        <input
          type="text"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          placeholder={t('approvals.field_target_id_hint')}
          required
          className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm num focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <span className="text-[11px] text-ink-500">
          {t('approvals.field_target_id_subhint')}
        </span>
      </label>

      <label className="block">
        <span className="text-xs text-ink-600">
          {t('approvals.field_signers')} <span className="text-warn-600">*</span>
        </span>
        <div className="mt-1">
          <MultiSelect
            value={signers}
            onChange={setSigners}
            options={memberOptions}
            placeholder={t('approvals.field_signers_hint')}
          />
        </div>
      </label>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100 disabled:opacity-60"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
        >
          {submitting ? t('common.creating') : t('common.create')}
        </button>
      </div>
    </form>
  );
}

function ApprovalDetail({
  approval,
  memberById,
  onApprove,
  onReject,
  busy,
}: {
  approval: Approval;
  memberById: Map<string, string>;
  onApprove: (note?: string) => void;
  onReject: (note?: string) => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState('');
  const nameOf = (id: string) =>
    memberById.get(id) || `${id.slice(0, 8)}…`;

  return (
    <div className="space-y-5 text-sm">
      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('approvals.drawer_overview')}
        </h3>
        <dl className="space-y-1.5">
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('approvals.col_target')}</dt>
            <dd className="text-ink-900 num">{approval.targetId}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('approvals.col_status')}</dt>
            <dd
              className={`text-[10px] uppercase tracking-wide font-semibold rounded-md px-2 py-0.5 ${STATUS_COLOR[approval.status]}`}
            >
              {t(STATUS_KEY[approval.status])}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('approvals.col_step')}</dt>
            <dd className="text-ink-900 num">
              {approval.currentStep + 1} / {approval.requiredSigners.length}
            </dd>
          </div>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('approvals.drawer_signers')}
        </h3>
        <ol className="space-y-1.5">
          {approval.requiredSigners.map((sid, i) => {
            const isCurrent = i === approval.currentStep;
            const isDone = i < approval.currentStep;
            return (
              <li
                key={sid}
                className={
                  'flex items-center gap-3 rounded-md border px-3 py-1.5 text-xs ' +
                  (isCurrent
                    ? 'border-accent-400 bg-accent-50'
                    : isDone
                      ? 'border-emerald-300 bg-emerald-50/40'
                      : 'border-ink-200 bg-white')
                }
              >
                <span className="num text-ink-500 w-5">{i + 1}.</span>
                <span className="flex-1 font-medium text-ink-900">{nameOf(sid)}</span>
                {isDone && (
                  <span className="text-[10px] uppercase text-emerald-700">
                    {t('approvals.signer_done')}
                  </span>
                )}
                {isCurrent && (
                  <span className="text-[10px] uppercase text-accent-700">
                    {t('approvals.signer_current')}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      {approval.history.length > 0 && (
        <section>
          <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
            {t('approvals.drawer_history')}
          </h3>
          <ol className="space-y-2">
            {approval.history.map((h, i) => (
              <li
                key={i}
                className="rounded-md border border-ink-200 bg-white px-3 py-2 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-ink-900">
                    {nameOf(h.user_id)}
                  </span>
                  <span
                    className={
                      'text-[10px] uppercase font-semibold ' +
                      (h.action === 'approved' ? 'text-emerald-700' : 'text-rose-700')
                    }
                  >
                    {t(`approvals.act_${h.action}`)}
                  </span>
                </div>
                <div className="text-ink-500 num mt-0.5">
                  {formatLocaleDate(h.at, i18n.language, { withTime: true })}
                </div>
                {h.note && (
                  <p className="text-ink-700 mt-1 whitespace-pre-wrap">{h.note}</p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {approval.status === 'pending' && (
        <section className="pt-2 border-t border-ink-200 space-y-3">
          <label className="block">
            <span className="text-xs text-ink-600">
              {t('approvals.field_note')}
            </span>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('approvals.field_note_hint')}
              className="mt-1 w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onApprove(note.trim() || undefined)}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {t('approvals.act_approve')}
            </button>
            <button
              type="button"
              onClick={() => onReject(note.trim() || undefined)}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
            >
              {t('approvals.act_reject')}
            </button>
          </div>
        </section>
      )}

      {approval.status === 'approved' && (
        <section className="pt-2 border-t border-ink-200">
          <button
            type="button"
            onClick={() => {
              approvalsApi.downloadHankoPdf(approval.id).catch(() => undefined);
            }}
            className="text-xs px-3 py-1.5 rounded border border-ink-300 text-ink-700 hover:bg-ink-50"
          >
            {t('approvals.download_hanko')}
          </button>
        </section>
      )}
    </div>
  );
}
